import { Avatar, FOCUS_RING } from '@amelio/ui-library';

import { MATRIX_EMPLOYEE_COLUMN_WIDTH, MATRIX_EMPLOYEE_JOB_TITLE } from '../reportStyles';

import type { CompetencyMatrixEmployee } from '@/types/reports';

import { useAmelioTranslations } from '@/hooks';
import { useNavigate } from '@/hooks/utils/useNavigate';
import { RouteName } from '@/navigation/types';
import { getInitials, getProfilePictureUrl } from '@/utils/user';

interface MatrixEmployeeHeaderProps {
    employee: CompetencyMatrixEmployee;
}

/** One employee column's header: avatar, name and job title, all inside a single button
 * (a clickable identity always goes to its main destination) that opens that employee's
 * performance profile. */
export const MatrixEmployeeHeader = ({ employee }: MatrixEmployeeHeaderProps) => {
    const { t } = useAmelioTranslations();
    const navigate = useNavigate();
    const fullName = `${employee.firstName} ${employee.lastName}`;

    const openProfile = () => {
        navigate({ path: RouteName.PerformanceProfile.replace(':id', employee.id) });
    };

    return (
        <button
            type={'button'}
            onClick={openProfile}
            aria-label={t('reports.competencyMatrix.matrix.openProfile', { name: fullName })}
            className={`${MATRIX_EMPLOYEE_COLUMN_WIDTH} ${FOCUS_RING} flex shrink-0 flex-col items-center gap-1 rounded-lg p-1 text-center hover:opacity-80`}
            data-testid={`matrix-employee-header-${employee.id}`}
        >
            <Avatar
                src={getProfilePictureUrl(employee.profilePictureUrl) || undefined}
                alt={fullName}
                initials={getInitials(employee)}
                bgColor={'purple'}
                // The prototype draws a 40px avatar, which is exactly what the '2xs' token renders
                // here (`h-10 w-10` against the module's 4px step). Nothing to override.
                size={'2xs'}
            />

            <span
                className={'text-navy w-full truncate text-xs font-medium'}
                title={fullName}
            >
                {fullName}
            </span>

            <span
                className={`${MATRIX_EMPLOYEE_JOB_TITLE} text-base-500 w-full truncate`}
                title={employee.jobTitle}
            >
                {employee.jobTitle}
            </span>
        </button>
    );
};
